import * as types from '../mutation-types';
import apiAuth from '../../api/modules/auth';
import { setAccessToken } from '../../api';
import { Module } from 'vuex';
import { isString } from 'lodash';
import { hasOwn, trim } from '@/core/utils/utils';

interface State {
  accessToken: string | null;
  role: string | null;
  localStudioPassword: string | null;
  user: UserResponse | null;
  teacher: TeacherResponse | null;
  userInfoPromise: Promise<UsersInfoResponse> | null;
  plans: PlanResponse | null;
  programme: CoachingProgrammeResponse | null;
  coupon: string | null;
  couponPlans: PlanResponse | null;
  couponDuration: CouponDurationResponse | null;
  campaign: string | null;
  zoomInfo: ZoomInfoResponse[] | null;
  googleMeetInfo: GoogleMeetInfoResponse[] | null;
  currency: string;
  subscriptions: StudentSubscriptionResponse[] | null;
  punches: StudentPunchesResponse[] | any[];
  purchases: StudentPurchasesResponse[] | any[];
  usersAndInvitations: UsersAndInvitationsResponse | null;
  teacherPermissions: TeacherPermissionsResponse | {};
  showUpgradeModal: boolean;
}

const initialState: State = {
  accessToken: null,
  localStudioPassword: null,
  role: null,
  user: null,
  teacher: null,
  userInfoPromise: null,
  plans: null,
  programme: null,
  coupon: null,
  couponDuration: null,
  couponPlans: null,
  campaign: null,
  zoomInfo: null,
  googleMeetInfo: null,
  currency: 'USD',
  subscriptions: null,
  punches: [],
  purchases: [],
  usersAndInvitations: null,
  teacherPermissions: {},
  showUpgradeModal: false,
};

const userModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    accessToken: state => {
      return state.accessToken;
    },
    canAccessLockedPages: state => {
      if (!state.teacher?.teacher) return;
      const { studio_password, enable_studio_password } = state.teacher.teacher;
      return (
        !enable_studio_password ||
        studio_password === btoa(state.localStudioPassword || '')
      );
    },
    isLoggedIn: state => {
      return !!state.accessToken;
    },
    user: state => state.user,
    teacherCurrency: state => state.teacher?.teacher?.currency,
    punches: state => state.punches,
    userId: state => state.user?.user_id,
    userHash: state => state.user?.user_hash,
    studentId: state => state.user?.student?.id,
    student: state => state.user?.student,
    studentPhone: state => state.user?.student?.phone,
    studentCommunityProfileId: state => state.user?.community_profile_id,
    studentCommunityProfileFirstName: state => state.user?.community_first_name,
    studentCommunityProfileLasttName: state => state.user?.community_last_name,
    studioCommunityProfileId: state =>
      state.teacher?.teacher?.community_profile_id,
    userFullName: state =>
      trim(state.user?.user_full_name) ||
      `${state.teacher?.teacher?.first_name} ${state.teacher?.teacher?.last_name}`,
    userPlans: ({ plans, coupon, couponPlans }) =>
      (coupon && couponPlans) || plans || [],
    couponDuration: state => state.couponDuration,
    programme: state => state.programme,
    teacher: state => state.teacher?.teacher,
    teacherMagicCode: state => state.teacher?.magic_code,
    studentMagicCode: state => state.user?.magic_code,
    isStudioOwner: state => state.teacher?.is_studio_owner,
    studioURL: state => state.teacher?.teacher?.studio_url,
    teacherPermissions: state => state.teacherPermissions,
    showUpgradeModal: state => state.showUpgradeModal,
    maxTeamSize: state => state.teacher?.teacher?.team_size,
    maxAdmins: state => state.teacher?.teacher?.max_admins,
    maxInstructors: state => state.teacher?.teacher?.max_instructors,
    canCreateWWWEvent: state => state.teacher?.teacher?.can_create_www_events,
    canPostFreeWWWEvent: state =>
      state.teacher?.teacher?.can_post_free_www_events,
    instagramConnected: state => state.teacher?.teacher?.instagram_connected,
    teacherInviteEmailsQuota: state =>
      state.teacher?.teacher?.invite_emails_quota,
    zoomInfo: state => state.zoomInfo,
    googleMeetInfo: state => state.googleMeetInfo,
    isZoomConnected: state => state.teacher?.teacher?.is_zoom_connected,
    isGoogleMeetConnected: state =>
      state.teacher?.teacher?.is_google_meet_connected,
    isTokboxEnabled: state => state.teacher?.teacher?.is_tokbox_enabled,
    isExternalAffiliate: state => state.teacher?.teacher?.is_external_affiliate,
    isBioAccount: state => state.teacher?.teacher?.is_bio,
    zoomWebinarsEnabled: state => state.teacher?.teacher?.zoom_webinars_enabled,
    teacherStudioUrl: state => state.teacher?.teacher.studio_url,
    studentsLastStudioUrl: state => state.user?.studio_url,
    showProductCustomFields: state =>
      state.teacher?.teacher?.zapier_custom_fields_enabled,
    teacherHasStripe: state =>
      state.teacher?.teacher?.payment_gateway === 'stripe',
    teacherHasPayPal: state =>
      state.teacher?.teacher?.payment_gateway === 'paypal',
    role: state => state.role,
    numberOfThisStudioPunches: (state, getters, rootState, rootGetters) =>
      state.punches.filter(
        p =>
          p.studio_url === rootGetters['studio/currentStudioUrl'] && !p.expired
      ).length,
    language: state => {
      return state.teacher?.language;
    },
    hasMonthlyPlans(state, getters) {
      return getters.userPlans.reduce(
        (sum, { monthly_amount }) => sum + parseInt(monthly_amount),
        0
      );
    },
    hasYearlyPlans(state, getters) {
      return getters.userPlans.reduce(
        (sum, { yearly_amount }) => sum + parseInt(yearly_amount),
        0
      );
    },
    hasSubscriptionType(state, getters) {
      return !!(getters.hasMonthlyPlans && getters.hasYearlyPlans);
    },
    hasMultiPlans(state, getters) {
      return getters.userPlans.length > 1;
    },
    usersAndInvitations: state => state.usersAndInvitations,
    currentStudioSubscriptions(
      state,
      getters,
      rootState,
      rootGetters
    ): StudentSubscriptionResponse[] | null {
      const currentStudioUrl = rootGetters['studio/currentStudioUrl'];
      if (currentStudioUrl && state.subscriptions) {
        return state.subscriptions?.filter(
          ({ studio_url }) => studio_url === currentStudioUrl
        );
      } else return null;
    },
    customDomainEmails: state =>
      state.teacher?.teacher?.custom_domain_email_links_enabled,
    customDomainWidgets: state =>
      state.teacher?.teacher?.custom_domain_widgets_enabled,
    timezone: state => state.user?.timezone,
  },
  actions: {
    async logout({ commit, dispatch }) {
      commit(types.SET_ACCESS_TOKEN, null);
      commit(types.SET_USER_INFO_PROMISE, null);
    },
    async register(
      { commit, dispatch },
      data: RegisterStudentRequest
    ): Promise<LoginResponse> {
      const loginResponse = await apiAuth.register(data);
      const { key } = loginResponse;
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
      return loginResponse;
    },
    async login({ commit }, { email, password }): Promise<LoginResponse> {
      const loginResponse = await apiAuth.login(email, password);
      const { key } = loginResponse;
      commit(types.SET_ACCESS_TOKEN, key);
      setAccessToken(key);
      return loginResponse;
    },
    async loginWithFacebook(
      { commit, dispatch },
      token: string
    ): Promise<LoginResponse> {
      const loginResponse = await apiAuth.facebookLogin(token);
      const { key } = loginResponse;
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
      return loginResponse;
    },
    async loginWithFacebookCustom(
      { commit, dispatch },
      { token, uid, email }
    ): Promise<void> {
      const { key } = await apiAuth.facebookCustomLogin(token, uid, email);
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
    },
    async loginWithMagicCode(
      { commit, dispatch },
      magicCode: string
    ): Promise<LoginResponse> {
      const loginResponse = await apiAuth.magicCodeLogin(magicCode);
      const { key } = loginResponse;
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
      return loginResponse;
    },
    async loginWithGoogle(
      { commit, dispatch },
      token: string
    ): Promise<LoginResponse> {
      const loginResponse = await apiAuth.googleLogin(token);
      const { key } = loginResponse;
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
      return loginResponse;
    },
    async loginWithGoogleCustom(
      { commit, dispatch },
      { token, uid, email }
    ): Promise<void> {
      const { key } = await apiAuth.googleCustomLogin(token, uid, email);
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
    },
    async loginWithApple(
      { commit, dispatch },
      { uid, email, fullName, idToken }
    ): Promise<void> {
      const { key } = await apiAuth.appleLogin(uid, email, fullName, idToken);
      commit(types.SET_ACCESS_TOKEN, key);
      dispatch('loadUserInfo');
      setAccessToken(key);
    },
    async changePassword(_, password: ChangePassword): Promise<any> {
      const result = await apiAuth.changePassword(
        password.newPassword1,
        password.newPassword2
      );
      return result;
    },
    async resetPassword(_, { email, studioURL }): Promise<any> {
      const res = await apiAuth.resetPassword(email, studioURL);
      return res;
    },
    resetState({ commit }): void {
      commit(types.RESET_USER_STATE);
    },
    loadUserInfo({ commit, dispatch, getters }) {
      const promise = apiAuth.loadUserInfo();
      promise.then(payload => {
        commit(types.SET_ROLE, payload.role);
        commit(types.SET_STUDENT, payload.student);
        commit(types.SET_TEACHER, payload.teacher);
        commit(types.SET_TEACHER_PERMISSIONS, payload.teacher_permissions);
        commit(types.SET_ZOOM_INFO, payload.zoom_info);
        commit(types.SET_GOOGLE_MEET_INFO, payload.google_meet_info);
        if (getters.isLoggedIn) {
          dispatch('studentPunches');
          dispatch('community/getNotificationsToken', null, { root: true });
          console.log('getters.role=', getters.role);
          if (getters.role === 'teacher') {
            dispatch('community/getStudioNotificationsToken', null, {
              root: true,
            });
            dispatch(
              'community/loadStudioNotifications',
              { markSeen: false },
              { root: true }
            );
          } else {
            dispatch(
              'community/loadNotifications',
              { markSeen: false },
              { root: true }
            );
          }
        }
      });
      commit(types.SET_USER_INFO_PROMISE, promise);
      return promise;
    },
    getLoginRedirectData({ state }, studioUrl) {
      if (state.role === 'student' || studioUrl) {
        const name = 'studio-dashboard';
        const params = { studio_url: studioUrl || state.user?.studio_url };
        return { name, params };
      } else {
        const name = 'dashboard';
        return { name };
      }
    },
    async meStudent({ commit }) {
      const student = await apiAuth.meStudent();
      commit(types.SET_STUDENT, student);
    },
    async meTeacher({ commit }) {
      const teacher = await apiAuth.meTeacher();
      commit(types.SET_TEACHER, teacher.teacher);
      return teacher;
    },
    async getStudioUsersAndInvitations({ commit }) {
      const studioUsersAndInvitations = await apiAuth.getStudioUsersAndInvitations();
      commit(types.SET_USERS_AND_INVITATIONS, studioUsersAndInvitations);
      return studioUsersAndInvitations;
    },
    async updateStudent({ commit }, user: UserResponse) {
      const student = await apiAuth.updateStudent(user);
      commit(types.SET_STUDENT, student);
    },
    async updateStudentDirect({ commit }, user: UserResponse) {
      await apiAuth.updateStudent(user);
    },
    async toggleStudentEventNotifications({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleStudentEventNotifications(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleStudentEmailNotifications({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleStudentEmailNotifications(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleStudentSMSNotifications({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleStudentSMSNotifications(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleStudentEmail30mins({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleStudentEmail30mins(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleStudentEmail1hr({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleStudentEmail1hr(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleCommunityDailyDigestEmail({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleCommunityDailyDigestEmail(toggle);
      commit(types.SET_STUDENT, student);
    },
    async toggleCommunityChatNotificationsEmail({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleCommunityChatNotificationsEmail(
        toggle
      );
      commit(types.SET_STUDENT, student);
    },
    async toggleCommunityRealtimeMentionsEmail({ commit }, toggle: boolean) {
      const student = await apiAuth.toggleCommunityRealtimeMentionsEmail(
        toggle
      );
      commit(types.SET_STUDENT, student);
    },
    async togglePrivatePodcastNotificationsEmail({ commit }, toggle: boolean) {
      const student = await apiAuth.togglePrivatePodcastNotificationsEmail(
        toggle
      );
      commit(types.SET_STUDENT, student);
    },
    async updateTeacher({ commit }, data: TeacherResponse) {
      const teacher = await apiAuth.updateTeacher(data);
      commit(types.SET_TEACHER, teacher);
    },
    async updateTeacherSignupInfo({ commit }, data: SignupInfoResponse) {
      return await apiAuth.updateTeacherSignupInfo(data);
    },
    async populateTeacherStudio(_) {
      return await apiAuth.populateTeacherStudio();
    },
    async getTeacherAnnouncement() {
      return await apiAuth.getTeacherAnnouncement();
    },
    async updateTeacherAnnouncement(_, data: TeacherAnnouncementResponse) {
      return await apiAuth.updateTeacherAnnouncement(data);
    },
    async toggleWaiver({ commit }, waiverState: boolean) {
      const teacher = await apiAuth.toggleTeacherWaiver(waiverState);
      commit(types.SET_TEACHER, teacher);
    },
    async toggleInstagramFeed({ commit }, toggle: boolean) {
      const teacher = await apiAuth.toggleTeacherInstagramFeed(toggle);
      commit(types.SET_TEACHER, teacher);
    },
    async disconnectInstagram({ commit }, toggle: boolean) {
      const teacher = await apiAuth.disconnectTeacherInstagram();
      commit(types.SET_TEACHER, teacher);
    },
    async teacherSubscribe(_, data) {
      await apiAuth.teacherSubscribe(data);
    },
    async hasPermission({ state }, permission = []) {
      await state.userInfoPromise;
      return state.teacherPermissions[permission];
    },
    async getPlans({ commit }, campaign) {
      commit(types.SET_CAMPAIGN, campaign);
      const plans = await apiAuth.getPlans(campaign);
      commit(types.SET_PLANS, plans);
      return plans;
    },
    async getCoachingProgramme({ commit }, programmeCode) {
      const coachingCrogramme = await apiAuth.getCoachingProgramme(
        programmeCode
      );
      commit(types.SET_COACHING_PROGRAMME, coachingCrogramme[0]);
      return coachingCrogramme;
    },
    async applyCoupon({ commit, getters }, { coupon, campaign }) {
      try {
        const response: any = await apiAuth.verifyCoupon(coupon, campaign);
        const couponPlans = response.plans;
        const couponDuration = response.coupon_duration;
        const plans: any = [];
        for (const key in couponPlans) {
          if (hasOwn(couponPlans, key)) {
            const id = key.split('_')[1];
            const plan = couponPlans[key];

            for (let i = 0; i < getters.userPlans.length; i++) {
              if (getters.userPlans[i].id == id) {
                const planCopy = {
                  ...getters.userPlans[i],
                  monthly_amount: plan.monthly,
                  yearly_amount: plan.yearly,
                  monthly_standard: plan.monthly_standard,
                  yearly_standard: plan.yearly_standard,
                  monthly_discounted: plan.monthly_discounted,
                  yearly_discounted: plan.yearly_discounted,
                  saveings:
                    getters.userPlans[i].monthly_amount * 12 -
                    getters.userPlans[i].yearly_amount,
                  yearlyPerMonth: getters.userPlans[i].yearly_amount / 12,
                };
                plans.push(planCopy);
              }
            }
          }
        }
        commit(types.SET_COUPON, {
          coupon,
          couponPlans: plans,
          couponDuration: couponDuration,
        });
      } catch (error) {
        commit(types.SET_COUPON);
        return Promise.reject(error);
      }
    },
    async applyProgrammeCoupon({ commit, getters }, { code, coupon }) {
      const couponPlans: any = await apiAuth.verifyProgrammeCoupon(
        code,
        coupon
      );
      const programme = {
        ...getters.programme,
        plan: couponPlans.plan,
        payment_plans: couponPlans.payment_plans,
        couponCode: coupon,
      };
      commit(types.SET_COACHING_PROGRAMME, programme);
    },
    clearCoupon({ commit }) {
      commit(types.SET_COUPON);
    },
    async getUserByUUID(_, uuid) {
      const user = await apiAuth.getUserByUUID(uuid);
      return user;
    },
    async updateUserByUUID(_, { uuid, user }) {
      const res = await apiAuth.updateUserByUUID(uuid, user);
      return res;
    },
    async resetConfirm(_, data) {
      const { new_password1, token, uid } = data;
      const res = await apiAuth.resetConfirm(new_password1, token, uid);
      return res;
    },
    async completeRegistration(_, { uuid, data }) {
      const res = await apiAuth.completeRegistration(uuid, data);
      return res;
    },
    async studentActiveSubscriptions({ commit }) {
      const subscriptions = await apiAuth.studentActiveSubscriptions();
      commit(types.SET_SUBSCRIPTIONS, subscriptions);
      return subscriptions;
    },
    async cancelStudentSubscription(_, id) {
      const subscription = await apiAuth.cancelStudentSubscription(id);
      return subscription;
    },
    async updateStudentSubscriptionPayment(_, { id, tk, type }) {
      await apiAuth.updateStudentSubscriptionPayment(id, tk, type);
    },
    async signWaiver(_, { studio_id, waiver_id, uuid }) {
      await apiAuth.signWaiver(studio_id, waiver_id, uuid);
    },
    async studentPunches({ commit }) {
      const punches = await apiAuth.studentPunches();
      commit(types.SET_PUNCHES, punches);
    },
    async studentPurchases({ commit }) {
      const purchases = await apiAuth.studentPurchases();
      commit(types.SET_PURCHASES, purchases);
    },
    async inviteAdmin(_, email) {
      const result = await apiAuth.inviteAdmin(email);
      return result;
    },
    async removeInvitation(_, invitationUniqueId) {
      await apiAuth.removeInvitation(invitationUniqueId);
    },
    async removeTeamMember(_, userId) {
      await apiAuth.removeTeamMember(userId);
    },
    async getInvitation(_, invitationUniqueId) {
      return await apiAuth.getInvitation(invitationUniqueId);
    },
    async acceptInvitation(_, invitationUniqueId) {
      return await apiAuth.acceptInvitation(invitationUniqueId);
    },
    async downloadStudentPurchaseInvoice(_, purchaseId) {
      return apiAuth.downloadStudentPurchaseInvoice(purchaseId);
    },
    async downloadStudentSubscriptionInvoice(_, purchaseId) {
      return apiAuth.downloadStudentSubscriptionInvoice(purchaseId);
    },
    async downloadTeacherPurchaseInvoice(_, purchaseId) {
      return apiAuth.downloadTeacherPurchaseInvoice(purchaseId);
    },
    async htmlToPdf(_, { html, title }) {
      return apiAuth.htmlToPdf(html, title);
    },
    async studentIdAttendances(_, query) {
      const { studentId, q, dateFrom, dateTo, page } = query;
      return apiAuth.studentIdAttendances(studentId, q, dateFrom, dateTo, page);
    },
    async getAffiliateInfo(_) {
      return await apiAuth.getAffiliateInfo();
    },
    async updateAffiliateInfo(_, payload) {
      return await apiAuth.updateAffiliateInfo(payload);
    },
    async studentIdAttendancesExportToCSV(_, query) {
      const { studentId, q, dateFrom, dateTo } = query;
      const data = await apiAuth.studentIdAttendancesExportToCSV(
        studentId,
        q,
        dateFrom,
        dateTo
      );
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `attendance-${studentId}.csv`);
    },
    showUpgradeModal({ commit }, show) {
      commit(types.SET_SHOW_UPGRADE_MODAL, show);
    },
  },
  mutations: {
    [types.SET_ACCESS_TOKEN](state: State, accessToken: string) {
      state.accessToken = accessToken;
    },
    [types.SET_SHOW_UPGRADE_MODAL](state: State, show: boolean) {
      state.showUpgradeModal = show;
    },
    [types.RESET_USER_STATE](state: State) {
      Object.assign(state, {}, initialState, {
        localStudioPassword: state.localStudioPassword,
      });
    },
    [types.SET_STUDENT](state: State, user: UserResponse) {
      state.user = user;
    },
    [types.SET_ROLE](state: State, role: string) {
      state.role = role;
    },
    [types.SET_SUBSCRIPTIONS](
      state: State,
      subscriptions: StudentSubscriptionResponse[]
    ) {
      state.subscriptions = subscriptions;
    },
    [types.SET_PUNCHES](state: State, punches: StudentPunchesResponse[]) {
      state.punches = punches;
    },
    [types.SET_PURCHASES](state: State, purchases: StudentPurchasesResponse[]) {
      state.purchases = purchases;
    },
    [types.SET_TEACHER](state: State, teacher: TeacherResponse) {
      state.teacher = teacher;
    },
    [types.SET_TEACHER_PERMISSIONS](
      state: State,
      teacherPermissions: TeacherPermissionsResponse
    ) {
      state.teacherPermissions = teacherPermissions;
    },
    [types.SET_USERS_AND_INVITATIONS](
      state: State,
      usersAndInvitations: UsersAndInvitationsResponse
    ) {
      state.usersAndInvitations = usersAndInvitations;
    },
    [types.SET_ZOOM_INFO](state: State, zoomInfo: ZoomInfoResponse[]) {
      state.zoomInfo = zoomInfo;
    },
    [types.SET_GOOGLE_MEET_INFO](
      state: State,
      googleMeetInfo: GoogleMeetInfoResponse[]
    ) {
      state.googleMeetInfo = googleMeetInfo;
    },
    [types.SET_USER_INFO_PROMISE](
      state: State,
      userInfoPromise: Promise<UsersInfoResponse>
    ) {
      state.userInfoPromise = userInfoPromise;
    },
    [types.SET_PLANS](state: State, plans: PlanResponse) {
      state.plans = plans;
    },
    [types.SET_COACHING_PROGRAMME](
      state: State,
      programme: CoachingProgrammeResponse
    ) {
      state.programme = programme;
    },
    [types.SET_CAMPAIGN](state: State, campaign) {
      state.campaign = campaign;
    },
    [types.SET_COUPON](
      state: State,
      data = { coupon: null, couponPlans: null, couponDuration: null }
    ) {
      state.coupon = data.coupon;
      state.couponPlans = data.couponPlans;
      state.couponDuration = data.couponDuration;
    },
    [types.SET_CURRENCY](state: State, currency) {
      state.currency = currency;
    },
    [types.SET_LOCAL_STUDIO_PASSWORD](state: State, password) {
      state.localStudioPassword = password;
    },
  },
};

export default userModule;
